import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, MessageCircle } from 'lucide-react';
import { landlordsApi, unitsApi } from '../../lib/api.js';
import { useActivityLog } from '../../hooks/useActivityLog.js';
import { LandlordDrawer } from '../../components/landlords/LandlordDrawer.js';
import { useToast } from '../../components/ui/Toast.js';
import { Modal } from '../../components/ui/Modal.js';
import { Button } from '../../components/ui/Button.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import type { UnitListItem } from '@imza/shared';

export const Route = createFileRoute('/_auth/kanban')({
  component: KanbanPage,
});

type StageId =
  | 'new'
  | 'sale'
  | 'sold'
  | 'rent'
  | 'rented'
  | 'potential'
  | 'unavailable'
  | 'no_sale'
  | 'no_answer'
  | 'wrong_number'
  | 'agent';

// Sherzod-specified palette 2026-05-03 (matches Badge.tsx + LandlordDrawer.tsx + landlords/index.tsx):
const STAGES: Array<{ id: StageId; label: string; color: string; bg: string }> = [
  { id: 'new',          label: 'New',          color: '#3b82f6', bg: 'bg-blue-50 border-blue-200' },
  { id: 'sale',         label: 'Sale',         color: '#22c55e', bg: 'bg-green-50 border-green-200' },
  { id: 'rent',         label: 'Rent',         color: '#f97316', bg: 'bg-orange-50 border-orange-200' },
  { id: 'potential',    label: 'Potential',    color: '#a855f7', bg: 'bg-purple-50 border-purple-200' },
  { id: 'agent',        label: 'Agent',        color: '#0ea5e9', bg: 'bg-sky-50 border-sky-200' },
  { id: 'no_answer',    label: 'No Answer',    color: '#eab308', bg: 'bg-yellow-50 border-yellow-200' },
  { id: 'no_sale',      label: 'No Sale',      color: '#ef4444', bg: 'bg-red-50 border-red-200' },
  { id: 'wrong_number', label: 'Wrong Number', color: '#b91c1c', bg: 'bg-red-50 border-red-300' },
  { id: 'unavailable',  label: 'Unavailable',  color: '#9ca3af', bg: 'bg-gray-50 border-gray-200' },
  { id: 'sold',         label: 'Sold',         color: '#ef4444', bg: 'bg-red-50 border-red-200' },
  { id: 'rented',       label: 'Rented',       color: '#be123c', bg: 'bg-rose-100 border-rose-300' },
];

const PAGE_SIZE = 100;

function KanbanPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedLandlordId, setSelectedLandlordId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [confirmMove, setConfirmMove] = useState<{
    unit: UnitListItem;
    toStage: StageId;
  } | null>(null);

  // Fetch per-stage units — one query per column
  const stageQueries = STAGES.map((stage) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery({
      queryKey: ['units', { stage: stage.id, pageSize: PAGE_SIZE }],
      queryFn: () =>
        landlordsApi.listUnits({
          stage: stage.id,
          sort: 'building',
          dir: 'asc',
          page: 1,
          pageSize: PAGE_SIZE,
        }),
      staleTime: 30_000,
    })
  );

  // Total units + unique landlord count for the page header
  const totalUnits = stageQueries.reduce((sum, q) => sum + (q.data?.total ?? 0), 0);

  const patchMutation = useMutation({
    mutationFn: ({ unitId, stage }: { unitId: number; stage: StageId }) =>
      unitsApi.patch(unitId, { stage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
      toast('Stage updated', 'success');
      setConfirmMove(null);
    },
    onError: (err: Error) => {
      toast(err.message, 'error');
      setConfirmMove(null);
    },
  });

  function handleDragStart(e: React.DragEvent, unitId: number) {
    e.dataTransfer.setData('unitId', String(unitId));
  }

  function handleDrop(e: React.DragEvent, targetStage: string) {
    e.preventDefault();
    setDragOver(null);
    const unitId = Number(e.dataTransfer.getData('unitId'));
    if (!unitId) return;

    // Find the unit across all stage queries
    for (const q of stageQueries) {
      const found = q.data?.items.find((u) => u.unitId === unitId);
      if (found && found.stage !== targetStage) {
        setConfirmMove({ unit: found, toStage: targetStage as StageId });
        return;
      }
    }
  }

  // Friendly label for a unit: "BA-R-179" or building + unit no, falling back to owner name
  function unitLabel(unit: UnitListItem): string {
    const parts: string[] = [];
    if (unit.building) parts.push(unit.building);
    if (unit.unitNo) parts.push(unit.unitNo);
    return parts.length > 0 ? parts.join(' · ') : unit.ownerName;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]">
      <div className="px-4 pt-4 pb-3 flex-shrink-0">
        <h1 className="text-xl font-bold text-primary-text">Kanban board</h1>
        <p className="text-sm text-secondary">
          {totalUnits.toLocaleString()} units &middot; drag cards to change stage
        </p>
      </div>

      {/* Columns */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-3 px-4 pb-4 h-full min-w-max">
          {STAGES.map((stage, idx) => {
            const q = stageQueries[idx]!;
            const cards = q.data?.items ?? [];
            const total = q.data?.total ?? 0;

            return (
              <div
                key={stage.id}
                className={`flex flex-col w-72 sm:w-80 rounded-xl border ${stage.bg} ${
                  dragOver === stage.id ? 'ring-2 ring-primary' : ''
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(stage.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column header */}
                <div className="px-3 py-2.5 border-b border-inherit flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="font-semibold text-primary-text text-sm">{stage.label}</span>
                    <span className="ml-auto text-xs font-mono text-secondary bg-white/70 px-2 py-0.5 rounded-full">
                      {total.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {q.isLoading ? (
                    <div className="space-y-2 p-1">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="bg-white border border-border rounded-lg p-3 space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      ))}
                    </div>
                  ) : cards.length === 0 ? (
                    <p className="text-xs text-secondary text-center py-8">Empty</p>
                  ) : (
                    cards.map((unit) => (
                      <KanbanCard
                        key={unit.unitId}
                        unit={unit}
                        onClick={() => setSelectedLandlordId(unit.landlordId)}
                        onDragStart={(e) => handleDragStart(e, unit.unitId)}
                      />
                    ))
                  )}
                  {total > PAGE_SIZE && (
                    <p className="text-xs text-secondary text-center py-2">
                      Showing first {PAGE_SIZE} of {total.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirm stage change */}
      <Modal
        open={confirmMove !== null}
        onClose={() => setConfirmMove(null)}
        title="Move unit to new stage?"
        size="sm"
      >
        {confirmMove && (
          <div className="space-y-4">
            <p className="text-sm text-primary-text">
              Move unit{' '}
              <strong>
                {[confirmMove.unit.building, confirmMove.unit.unitNo]
                  .filter(Boolean)
                  .join(' · ') || confirmMove.unit.ownerName}
              </strong>{' '}
              to{' '}
              <strong>{STAGES.find((s) => s.id === confirmMove.toStage)?.label}</strong>?
            </p>
            <p className="text-xs text-secondary">This will be logged in the activity timeline.</p>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                loading={patchMutation.isPending}
                onClick={() =>
                  patchMutation.mutate({
                    unitId: confirmMove.unit.unitId,
                    stage: confirmMove.toStage,
                  })
                }
              >
                Move
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1"
                onClick={() => setConfirmMove(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <LandlordDrawer landlordId={selectedLandlordId} onClose={() => setSelectedLandlordId(null)} />
    </div>
  );
}

function KanbanCard({
  unit,
  onClick,
  onDragStart,
}: {
  unit: UnitListItem;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
}) {
  const { logAndCall, logAndWhatsApp } = useActivityLog();
  const phone = unit.primaryMobile;
  const isMasked = phone ? phone.phone.includes('*') : true;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="bg-white border border-border rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-primary/40 hover:shadow-sm transition-all"
    >
      <button
        onClick={onClick}
        className="block w-full text-left"
        aria-label={`Open landlord for unit ${unit.unitNo ?? 'unknown'}`}
      >
        {/* Primary line: unit reference */}
        <p className="font-medium text-primary-text text-sm truncate">
          {unit.unitNo
            ? `${unit.building ? unit.building + ' · ' : ''}${unit.unitNo}`
            : (unit.building ?? unit.ownerName)}
        </p>
        {/* Secondary line: owner name (small) */}
        <p className="text-xs text-secondary mt-0.5 truncate">{unit.ownerName}</p>
        {/* Tertiary: beds + view metadata */}
        {(unit.beds || unit.view) && (
          <p className="text-xs text-tertiary mt-0.5 truncate">
            {[unit.beds, unit.view].filter(Boolean).join(' · ')}
          </p>
        )}
      </button>

      {phone && !isMasked && (
        <div className="flex items-center gap-1 mt-2">
          <p className="text-xs font-mono text-secondary flex-1 truncate">{phone.phone}</p>
          <a
            href={`tel:${phone.phone}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              logAndCall(unit.landlordId, phone.phone);
            }}
            className="p-1.5 rounded-lg bg-success/10 text-success min-h-[32px] min-w-[32px] flex items-center justify-center"
            aria-label={`Call ${unit.ownerName}`}
          >
            <Phone className="w-3.5 h-3.5" />
          </a>
          <a
            href={`https://wa.me/${phone.phone.replace(/^\+/, '').replace(/\s/g, '')}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              logAndWhatsApp(unit.landlordId, phone.phone);
            }}
            className="p-1.5 rounded-lg bg-whatsapp/10 text-whatsapp min-h-[32px] min-w-[32px] flex items-center justify-center"
            aria-label={`WhatsApp ${unit.ownerName}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </div>
  );
}
